import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import { DeleteIcon, ImageIcon, PlusIcon, WarningIcon } from '@repo/ui/icons'
import InformationBox from '@repo/ui/information-box'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import TextInput from '../../../../../../../packages/ui/components/atoms/input/text-input'
import { useSecureFile } from '../../../../lib/hooks/use-secure-file'
import useTranslate from '../../../../lib/hooks/use-translate'
import { PendingSecureFile } from '../../../../lib/types/models/file/file-input'

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif']

type SecureImageItem = {
  uploadId?: string
  file?: File
  caption?: string
  name?: string
  size?: number
  previewUrl?: string
  isUploading?: boolean
}

type ImageCardProps = {
  item: SecureImageItem
  index: number
  onCaptionChange: (text: string) => void
  onFileSelected: (file: File) => void
  onImageDelete: () => void
  onCardDelete: () => void
}

function ImageCard({
  item,
  index,
  onCaptionChange,
  onFileSelected,
  onImageDelete,
  onCardDelete
}: ImageCardProps) {
  const { t } = useTranslate()
  const {
    url: signedUrl,
    status: scanStatus,
    isLoading: isScanLoading
  } = useSecureFile(item.uploadId)
  const displayUrl = item.previewUrl ?? signedUrl
  const isError = scanStatus === 'INFECTED' || scanStatus === 'FAILED'
  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <InnerBox className="p-4 rounded-xl flex flex-col gap-2 mobile:p-3">
      <TextInput
        type="text"
        autoComplete={`caption-${index}`}
        label={t('image.card.caption.label')}
        value={item.caption ?? ''}
        onChange={(e) => onCaptionChange(e.target.value)}
      />
      <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
        {t('image.card.upload.label')}
      </Typography>
      {item.isUploading || isScanLoading ? (
        <InnerBox className="bg-white-hover rounded-lg p-2 h-40 flex flex-col items-center justify-center gap-2">
          <Loader />
        </InnerBox>
      ) : isError ? (
        <InnerBox className="bg-red-50 rounded-lg p-2 h-40 flex flex-col items-center justify-center gap-2">
          <WarningIcon color={tailwindColors.secondaryRed.DEFAULT} />
          <Typography variant="label-semibold" color={tailwindColors.secondaryRed.DEFAULT}>
            {scanStatus === 'INFECTED' ? 'Virus détecté' : "Échec de l'analyse"}
          </Typography>
          <ActionButton icon={DeleteIcon} variant="danger" onClick={onCardDelete} />
        </InnerBox>
      ) : displayUrl ? (
        <InnerBox className="bg-white-hover rounded-lg p-2">
          <img src={displayUrl} alt="Image" className="w-full h-40 object-cover rounded-lg" />
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <Typography
                variant="tag"
                color={tailwindColors.grey.DEFAULT}
                className="truncate max-w-[120px] overflow-hidden"
              >
                {item.name ?? 'Image'}
              </Typography>
              {item.size != null && (
                <Typography variant="label" color={tailwindColors.grey.light.active}>
                  {(item.size / 1024).toFixed(0)}KB
                </Typography>
              )}
            </div>
            <ActionButton icon={DeleteIcon} variant="danger" onClick={onImageDelete} />
          </div>
        </InnerBox>
      ) : (
        <>
          <input
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) onFileSelected(file)
              e.target.value = ''
            }}
          />
          <div
            className="w-full px-1 h-40 flex flex-col relative items-center justify-center rounded-lg bg-transparent border border-dashed border-grey-light-active cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              const file = e.dataTransfer.files?.[0]
              if (!file) return
              if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                toast.error(t('images.read.error'))
                return
              }
              onFileSelected(file)
            }}
          >
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-lg p-2">
              <div className="bg-primary-light p-4 rounded-3xl">
                <ImageIcon className="h-7 w-7 fill-primary" />
              </div>
              <Typography
                variant="label"
                className="text-center"
                color={tailwindColors.grey.DEFAULT}
              >
                {t('image.card.drag.label')}
              </Typography>
            </div>
          </div>
          <ActionButton
            className="w-full"
            icon={DeleteIcon}
            variant="danger"
            onClick={onCardDelete}
          />
        </>
      )}
    </InnerBox>
  )
}

type SecureFamilyImagesUploaderProps = {
  images: Partial<PendingSecureFile>[]
  onChange: (images: PendingSecureFile[]) => void
  mode: 'primary' | 'secondary'
}

const SecureFamilyImagesUploader = ({
  images,
  onChange,
  mode
}: SecureFamilyImagesUploaderProps) => {
  const { t } = useTranslate()

  const [items, setItems] = useState<SecureImageItem[]>(() =>
    images.map((img) => ({
      uploadId: img.uploadId,
      file: img.file,
      caption: img.caption,
      name: img.name,
      size: img.file ? img.file.size : undefined
    }))
  )

  // Ref keeps the latest items accessible inside async handlers without stale closures
  const itemsRef = useRef(items)

  const emitChange = (newItems: SecureImageItem[]) => {
    const mapped: PendingSecureFile[] = newItems.map((item) => ({
      uploadId: item.uploadId,
      file: item.file,
      caption: item.caption,
      name: item.name
    }))
    onChange(mapped)
  }

  const updateItems = (newItems: SecureImageItem[]) => {
    itemsRef.current = newItems
    setItems(newItems)
    emitChange(newItems)
  }

  const handleCaptionChange = (index: number, text: string) => {
    const next = [...itemsRef.current]
    next[index] = { ...next[index], caption: text }
    updateItems(next)
  }

  const handleFileSelected = (index: number, file: File) => {
    if (file.size >= 2 * 1024 * 1024) {
      toast.error(t('images.size.error'))
      return
    }

    const previewUrl = URL.createObjectURL(file)
    const next = [...itemsRef.current]
    next[index] = {
      ...next[index],
      file,
      name: file.name,
      size: file.size,
      previewUrl,
      isUploading: false
    }
    updateItems(next)
  }

  const handleCardDelete = (index: number) => {
    const next = itemsRef.current.filter((_, i) => i !== index)
    updateItems(next)
  }

  const handleImageDelete = (index: number) => {
    handleCardDelete(index)
  }

  return (
    <InnerBox className="p-4 rounded-xl flex flex-col gap-2">
      <Typography
        variant="text"
        color={mode === 'primary' ? tailwindColors.black.DEFAULT : tailwindColors.primary.DEFAULT}
      >
        {t('affected.staff.questionnaire.host.family.pictures.title')}
      </Typography>

      <InformationBox isHiddenOnMobile={false}>
        <div className="flex flex-col gap-2">
          <Typography variant="caption" color={tailwindColors.black.DEFAULT}>
            {t('affected.staff.questionnaire.host.family.pictures.note.1')}
          </Typography>
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            <strong className="text-black">
              {t('affected.staff.questionnaire.host.family.pictures.note.2')}
            </strong>
            {mode === 'primary'
              ? t('host.family.images.note')
              : t('affected.staff.questionnaire.host.family.pictures.note.3')}
          </Typography>
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            <strong className="text-black">
              {t('affected.staff.questionnaire.host.family.pictures.note.nb')}
            </strong>{' '}
            {t('affected.staff.questionnaire.host.family.pictures.note.nb.text')}
          </Typography>
        </div>
      </InformationBox>

      <div className="grid grid-cols-3 gap-3 mobile:grid-cols-1">
        {items.map((item, index) => (
          <ImageCard
            key={`secure-image-${index}`}
            item={item}
            index={index}
            onCaptionChange={(text) => handleCaptionChange(index, text)}
            onFileSelected={(file) => handleFileSelected(index, file)}
            onImageDelete={() => handleImageDelete(index)}
            onCardDelete={() => handleCardDelete(index)}
          />
        ))}
      </div>

      <Button
        label={t('affected.staff.questionnaire.host.family.pictures.upload.label.btn')}
        icon={PlusIcon}
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => {
          const next = [...itemsRef.current, { caption: '' }]
          itemsRef.current = next
          setItems(next)
        }}
      />
    </InnerBox>
  )
}

export default SecureFamilyImagesUploader
